// Eingabe und Anzeige persönlicher Arbeitszeiten je Wochentag.
//
// Hintergrund: Krumme Wochenstunden (Sarah: 22 h) passen in kein Modell. Statt für
// jeden Sonderfall ein Modell anzulegen, stehen die Zeiten direkt an der Zuordnung
// der Person (Entscheidung Björn 2026-08-13); die Modelle bleiben Vorlagen zum
// Vorbelegen. Diese Datei kennt die Umrechnung — der Rechenkern (berechnung.ts)
// bekommt am Ende nur noch fertige Minuten je Wochentag.

import type { Arbeitszeitmodell, SollProTag } from './berechnung';

export const WOCHENTAGE: { schluessel: string; kurz: string; lang: string }[] = [
  { schluessel: '1', kurz: 'Mo', lang: 'Montag' },
  { schluessel: '2', kurz: 'Di', lang: 'Dienstag' },
  { schluessel: '3', kurz: 'Mi', lang: 'Mittwoch' },
  { schluessel: '4', kurz: 'Do', lang: 'Donnerstag' },
  { schluessel: '5', kurz: 'Fr', lang: 'Freitag' },
  { schluessel: '6', kurz: 'Sa', lang: 'Samstag' },
  { schluessel: '7', kurz: 'So', lang: 'Sonntag' },
];

const TAG_MAX = 24 * 60;

/**
 * Stundeneingabe zu Minuten. Erlaubt ist, was Menschen tippen: „8", „5:30", „5,5",
 * „5.5". Unlesbares und Negatives ergibt null — der Aufrufer entscheidet, ob das
 * „nichts eingegeben" oder „0 Stunden" heißt.
 */
export function minutenAusEingabe(roh: string): number | null {
  const wert = String(roh ?? '').trim().replace(',', '.');
  if (!wert) return null;

  const mitDoppelpunkt = wert.match(/^(\d+):([0-5]?\d)$/);
  if (mitDoppelpunkt) {
    const minuten = Number(mitDoppelpunkt[1]) * 60 + Number(mitDoppelpunkt[2]);
    return Math.min(TAG_MAX, minuten);
  }

  if (!/^\d+(\.\d+)?$/.test(wert)) return null;
  const stunden = Number(wert);
  if (!Number.isFinite(stunden) || stunden < 0) return null;
  return Math.min(TAG_MAX, Math.round(stunden * 60));
}

/**
 * Minuten als Feldinhalt „5:30" — bewusst OHNE „h", anders als `stundenText` aus
 * berechnung.ts (das ist für Anzeigen mit Vorzeichen gedacht). Was hier herauskommt,
 * muss `minutenAusEingabe` wieder verstehen.
 */
export function alsEingabe(minuten: number): string {
  const gesamt = Math.max(0, Math.round(minuten || 0));
  return `${Math.floor(gesamt / 60)}:${String(gesamt % 60).padStart(2, '0')}`;
}

/** Wochenarbeitszeit als Summe der sieben Tage. */
export function wochenMinuten(soll: SollProTag | null | undefined): number {
  return WOCHENTAGE.reduce((summe, t) => {
    const wert = Number(soll?.[t.schluessel] ?? 0);
    return summe + (Number.isFinite(wert) ? Math.max(0, wert) : 0);
  }, 0);
}

/** Zeiten einer Vorlage übernehmen — alle sieben Tage belegt, keine Lücken. */
export function sollAusModell(modell: Arbeitszeitmodell | undefined | null): SollProTag {
  const raus: SollProTag = {};
  for (const t of WOCHENTAGE) {
    const wert = Number(modell?.soll_minuten?.[t.schluessel] ?? 0);
    raus[t.schluessel] = Number.isFinite(wert) ? Math.max(0, wert) : 0;
  }
  return raus;
}
